import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit2, Trash2, UserX, Check, X } from 'lucide-react';
import { blacklistApi, Blacklist, CreateBlacklistDto } from '../api/blacklist';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';

export default function BlacklistPage() {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Blacklist | null>(null);
  const [formData, setFormData] = useState<CreateBlacklistDto>({
    guestName: '',
    guestPhone: '',
    reason: '',
  });
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  // Toast auto-dismiss with cleanup
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
  };

  // 블랙리스트 조회
  const { data: blacklist = [], isLoading } = useQuery({
    queryKey: ['blacklist'],
    queryFn: blacklistApi.getAll,
  });

  // 필터링된 목록
  const filteredList = showInactive
    ? blacklist
    : blacklist.filter((item) => item.isActive);

  // 블랙리스트 추가
  const createMutation = useMutation({
    mutationFn: blacklistApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blacklist'] });
      closeModal();
      showToast('블랙리스트에 추가되었습니다.');
    },
    onError: () => {
      showToast('추가에 실패했습니다.', 'error');
    },
  });

  // 블랙리스트 수정
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      blacklistApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blacklist'] });
      closeModal();
      showToast('수정되었습니다.');
    },
    onError: () => {
      showToast('수정에 실패했습니다.', 'error');
    },
  });

  // 블랙리스트 삭제
  const deleteMutation = useMutation({
    mutationFn: blacklistApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blacklist'] });
      showToast('삭제되었습니다.');
    },
    onError: () => {
      showToast('삭제에 실패했습니다.', 'error');
    },
  });

  // 블랙리스트 활성화/비활성화
  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      isActive ? blacklistApi.deactivate(id) : blacklistApi.activate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blacklist'] });
      showToast('상태가 변경되었습니다.');
    },
    onError: () => {
      showToast('상태 변경에 실패했습니다.', 'error');
    },
  });

  const openModal = (item?: Blacklist) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        guestName: item.guestName,
        guestPhone: item.guestPhone,
        reason: item.reason || '',
      });
    } else {
      setEditingItem(null);
      setFormData({ guestName: '', guestPhone: '', reason: '' });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
    setFormData({ guestName: '', guestPhone: '', reason: '' });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.guestName || !formData.guestPhone) {
      showToast('이름과 전화번호를 입력해주세요.', 'error');
      return;
    }

    if (editingItem) {
      updateMutation.mutate({ id: editingItem.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (item: Blacklist) => {
    if (window.confirm(`${item.guestName}님을 블랙리스트에서 삭제하시겠습니까?`)) {
      deleteMutation.mutate(item.id);
    }
  };

  const handleToggleActive = (item: Blacklist) => {
    toggleActiveMutation.mutate({ id: item.id, isActive: item.isActive });
  };

  const formatPhone = (phone: string) => {
    if (phone.length === 11) {
      return `${phone.slice(0, 3)}-${phone.slice(3, 7)}-${phone.slice(7)}`;
    }
    return phone;
  };

  if (isLoading) {
    return <LoadingSpinner className="py-16" />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <UserX className="w-6 h-6 text-red-600" />
          <h1 className="text-2xl font-bold text-gray-900">블랙리스트</h1>
          <span className="text-sm text-gray-500">({filteredList.length}명)</span>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="w-4 h-4"
            />
            비활성 포함
          </label>
          <button
            onClick={() => openModal()}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            <Plus className="w-4 h-4" />
            추가
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">이름</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">전화번호</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">사유</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">상태</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">등록일</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">관리</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredList.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    등록된 블랙리스트가 없습니다.
                  </td>
                </tr>
              ) : (
                filteredList.map((item) => (
                  <tr
                    key={item.id}
                    className={`hover:bg-gray-50 ${!item.isActive ? 'bg-gray-100 opacity-60' : ''}`}
                  >
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {item.guestName}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {formatPhone(item.guestPhone)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                      {item.reason || '-'}
                    </td>
                    <td className="px-4 py-3">
                      {item.isActive ? (
                        <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded">
                          활성
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium bg-gray-200 text-gray-600 rounded">
                          비활성
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {format(new Date(item.createdAt), 'yyyy.MM.dd', { locale: ko })}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleToggleActive(item)}
                          className={`p-1.5 rounded hover:bg-gray-200 ${
                            item.isActive ? 'text-gray-500' : 'text-green-600'
                          }`}
                          title={item.isActive ? '비활성화' : '활성화'}
                        >
                          {item.isActive ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => openModal(item)}
                          className="p-1.5 text-blue-600 rounded hover:bg-blue-50"
                          title="수정"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(item)}
                          className="p-1.5 text-red-600 rounded hover:bg-red-50"
                          title="삭제"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">
                {editingItem ? '블랙리스트 수정' : '블랙리스트 추가'}
              </h2>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="px-6 py-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    이름 *
                  </label>
                  <input
                    type="text"
                    value={formData.guestName}
                    onChange={(e) =>
                      setFormData({ ...formData, guestName: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    placeholder="예: 홍길동"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    전화번호 *
                  </label>
                  <input
                    type="text"
                    value={formData.guestPhone}
                    onChange={(e) =>
                      setFormData({ ...formData, guestPhone: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    placeholder="예: 01012345678"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    사유
                  </label>
                  <textarea
                    value={formData.reason}
                    onChange={(e) =>
                      setFormData({ ...formData, reason: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    rows={3}
                    placeholder="블랙리스트 등록 사유를 입력하세요"
                  />
                </div>
              </div>
              <div className="px-6 py-4 border-t flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-gray-700 border rounded-lg hover:bg-gray-50"
                >
                  취소
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  {editingItem ? '수정' : '추가'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg text-white z-50 ${
            toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}
